import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { environment } from "../../../environment";
import { IngredientsAnalyticsResponse, SortDirection } from "../types";
import { buildAnalyticsParams } from "./buildAnalyticsParams";
import { SharedAnalyticsFilters } from "./useAnalyticsFilters";

const ENDPOINT = `${environment.VITE_URL_SCHEME}${environment.VITE_SERVER_DOMAIN}/api/v1/analytics/ingredients`;

// Shared filters + the Ingredients tab's own table controls.
export type IngredientsAnalyticsFilters = SharedAnalyticsFilters & {
  sortBy: string;
  sortDir: SortDirection;
  page: number;
  pageSize: number;
};

const buildParams = (f: IngredientsAnalyticsFilters): Record<string, string> => ({
  ...buildAnalyticsParams(f),
  page: String(f.page),
  pageSize: String(f.pageSize),
});

export interface UseIngredientsAnalytics {
  data: IngredientsAnalyticsResponse | null;
  loading: boolean;
  error: boolean;
}

export const useIngredientsAnalytics = (
  filters: IngredientsAnalyticsFilters,
  { enabled }: { enabled: boolean }
): UseIngredientsAnalytics => {
  const [data, setData] = useState<IngredientsAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const requestIdRef = useRef(0);

  const params = buildParams(filters);
  const paramsKey = JSON.stringify(params);
  const customIncomplete = filters.timeRange === "custom" && (!filters.startDate || !filters.endDate);

  useEffect(() => {
    if (!enabled || customIncomplete) {
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    axios
      .get<IngredientsAnalyticsResponse>(ENDPOINT, { params, withCredentials: true })
      .then(res => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(true);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, customIncomplete, enabled]);

  return { data, loading, error };
};
